/**
 * Tenant probe API (issue #3 / #11) — typed wrapper over `GET /tenant/me`.
 *
 * Used by the #11 course form for the meeting-provider `Select` options
 * (`meeting.enabled_providers`) and the location `Input`'s datalist
 * suggestions (`locations` — empty today for both tenants; the form falls
 * back to plain free text, satisfying AC2's zero-config requirement).
 */
import { memberApi } from "@/lib/api/client";

// Where a scheduled class meets. Google Meet is a destination only.
export type MeetingProvider =
  | "zoom"
  | "google_meet"
  | "in_person"
  | "other";

export interface TenantMe {
  recording_policy?: RecordingPolicy | null;
  user_id: string;
  tenant_id: string;
  tenant_slug: string | null;
  tenant_name: string;
  role: string | null;
  meeting: {
    enabled_providers: MeetingProvider[];
    default_meeting_provider: MeetingProvider;
  };
  locations: string[];
  /**
   * Issue #321 Milestone 1. ONE derived boolean, deliberately not a generic
   * `features` map: a bag would let any later flag reach the client by
   * accident, and this one is a release control whose exposure is a decision.
   *
   * It decides what the app LINKS TO. It is not the security boundary — every
   * portal route on the server re-resolves the same flag, so hiding navigation
   * and refusing the route are separate mechanisms and only the second one is
   * authorization.
   */
  session_portal_enabled: boolean;
  /** Server-derived tenant onboarding policy for existing Workspace accounts. */
  existing_workspace_account_linking_enabled: boolean;
  /** Whether this tenant reads Classroom at all (Mufid only); its routes still 404 elsewhere. */
  classroom_references_enabled: boolean;
}

export interface RecordingPolicy {
  version: string;
  standing_notice: string;
  class_notice: string;
  support_email: string;
  /** Which recording journey the tenant has; absent until the custody row is read. */
  custody?: "provider_discovery" | "provider_link";
  signin_required?: boolean;
  audience_sentence?: string | null;
}

export function getTenantMe(): Promise<TenantMe> {
  return memberApi.get<TenantMe>("/tenant/me");
}
